import { CONSTELLATION_DOT_COUNT, CONSTELLATION_SIZE, FULL_ROTATION_RADIANS } from '../constants';
import type { ConstellationDot } from '../types';
import {
	getCycleProgress,
	getGlowSparkStrength,
	getSineWaveStrength,
	getStableVariation,
	getWrappedCycleDistance,
} from './poseHelpers';

type CursorDotInput = Readonly<{
	color: string;
	index: number;
	timeSeconds: number;
}>;

type CursorPosition = readonly [number, number];

type CursorSegment = Readonly<{
	from: CursorPosition;
	length: number;
	to: CursorPosition;
}>;

const CURSOR_REFERENCE_WIDTH = 13.8;
const CURSOR_REFERENCE_HEIGHT = 14.2;
const CURSOR_BOX_RATIO = 0.72;
const CURSOR_APEX_POSITION_INDEX = 0;

const HIGHLIGHT_CYCLES_AROUND_CURSOR = 2;
const HIGHLIGHT_SPEED_RADIANS_PER_SECOND = 1.7;
const HIGHLIGHT_PHASE_VARIATION = 0.6;
const HIGHLIGHT_BRIGHTNESS = 0.26;
const BASE_BRIGHTNESS = 0.42;

const CLICK_BRIGHTNESS = 0.55;
const CLICK_PERIOD_SECONDS = 2.3;
const CLICK_GAUSSIAN_WIDTH = 0.075;
const CLICK_TRAVEL_REFERENCE_PIXELS = 2.4;
const CLICK_TRAVEL_STRENGTH = 0.7;

// Controls how tightly the glow follows the highlight traveling around the cursor outline.
const HIGHLIGHT_GLOW_CONCENTRATION_POWER = 1.5;
const HIGHLIGHT_GLOW_STRENGTH = 1;
const APEX_CLICK_GLOW_STRENGTH = 0.5;

const SWAY_REFERENCE_PIXELS = 0.55;
const HORIZONTAL_SWAY_SPEED_RADIANS_PER_SECOND = 1.3;
const HORIZONTAL_SWAY_PHASE_OFFSET_RADIANS = 1.7;
const VERTICAL_SWAY_SPEED_RADIANS_PER_SECOND = 1.1;
const VERTICAL_SWAY_PHASE_OFFSET_RADIANS = 1.3;

const MINIMUM_APEX_DISTANCE_PIXELS = 1;
const APEX_VISIBILITY_OFFSET_PIXELS = 0.6;

const CURSOR_VERTICES = [
	[0, 0],
	[CURSOR_REFERENCE_WIDTH, 7.6],
	[8, 9.2],
	[5.9, CURSOR_REFERENCE_HEIGHT],
] as const;

const CURSOR_SEGMENTS: readonly CursorSegment[] = CURSOR_VERTICES.map((from, index) => {
	const to = CURSOR_VERTICES[(index + 1) % CURSOR_VERTICES.length];

	return {
		from,
		length: Math.hypot(to[0] - from[0], to[1] - from[1]),
		to,
	};
});
const CURSOR_PERIMETER = CURSOR_SEGMENTS.reduce((total, segment) => total + segment.length, 0);
const REFERENCE_TO_CONSTELLATION_SCALE =
	(CONSTELLATION_SIZE * CURSOR_BOX_RATIO) / CURSOR_REFERENCE_HEIGHT;
const CURSOR_OFFSET_X =
	(CONSTELLATION_SIZE - CURSOR_REFERENCE_WIDTH * REFERENCE_TO_CONSTELLATION_SCALE) / 2;
const CURSOR_OFFSET_Y =
	(CONSTELLATION_SIZE - CURSOR_REFERENCE_HEIGHT * REFERENCE_TO_CONSTELLATION_SCALE) / 2;

const getPositionOnSegment = (segment: CursorSegment, progress: number): CursorPosition => [
	CURSOR_OFFSET_X +
		(segment.from[0] + (segment.to[0] - segment.from[0]) * progress) *
			REFERENCE_TO_CONSTELLATION_SCALE,
	CURSOR_OFFSET_Y +
		(segment.from[1] + (segment.to[1] - segment.from[1]) * progress) *
			REFERENCE_TO_CONSTELLATION_SCALE,
];

const getPointAlongCursor = (perimeterDistance: number): CursorPosition => {
	let remainingDistance = perimeterDistance;

	for (const segment of CURSOR_SEGMENTS) {
		if (remainingDistance <= segment.length) {
			return getPositionOnSegment(segment, remainingDistance / segment.length);
		}

		remainingDistance -= segment.length;
	}

	const finalSegment = CURSOR_SEGMENTS[CURSOR_SEGMENTS.length - 1];
	return getPositionOnSegment(finalSegment, 1);
};

const CURSOR_BASE_POSITIONS = Array.from({ length: CONSTELLATION_DOT_COUNT }, (_, index) =>
	getPointAlongCursor((index / CONSTELLATION_DOT_COUNT) * CURSOR_PERIMETER),
);

const getClickStrength = (timeSeconds: number): number => {
	const cycleProgress = getCycleProgress(timeSeconds, CLICK_PERIOD_SECONDS);
	const distanceFromClick = getWrappedCycleDistance(cycleProgress, 0);

	// Gaussian pulse: creates a short, smooth click at the start of each cycle.
	return Math.exp(-0.5 * (distanceFromClick / CLICK_GAUSSIAN_WIDTH) ** 2);
};

const getHighlightStrength = (index: number, timeSeconds: number): number => {
	const positionProgress = index / CONSTELLATION_DOT_COUNT;

	return getSineWaveStrength(
		positionProgress * FULL_ROTATION_RADIANS * HIGHLIGHT_CYCLES_AROUND_CURSOR -
			timeSeconds * HIGHLIGHT_SPEED_RADIANS_PER_SECOND +
			HIGHLIGHT_PHASE_VARIATION * getStableVariation(index),
	);
};

const getAnimatedCursorPosition = (
	index: number,
	timeSeconds: number,
	clickStrength: number,
): CursorPosition => {
	const [baseX, baseY] = CURSOR_BASE_POSITIONS[index];
	const [apexX, apexY] = CURSOR_BASE_POSITIONS[CURSOR_APEX_POSITION_INDEX];
	const distanceToApex = Math.max(
		MINIMUM_APEX_DISTANCE_PIXELS,
		Math.hypot(apexX - baseX, apexY - baseY),
	);
	const clickTravelDistance =
		CLICK_TRAVEL_REFERENCE_PIXELS *
		(CONSTELLATION_SIZE / 48) *
		CLICK_TRAVEL_STRENGTH *
		clickStrength;
	const swayDistance = SWAY_REFERENCE_PIXELS * (CONSTELLATION_SIZE / 48);
	const apexOffset = index === CURSOR_APEX_POSITION_INDEX ? -APEX_VISIBILITY_OFFSET_PIXELS : 0;

	return [
		baseX +
			apexOffset +
			Math.sin(
				timeSeconds * HORIZONTAL_SWAY_SPEED_RADIANS_PER_SECOND +
					index * HORIZONTAL_SWAY_PHASE_OFFSET_RADIANS,
			) *
				swayDistance +
			((apexX - baseX) / distanceToApex) * clickTravelDistance,
		baseY +
			apexOffset +
			Math.cos(
				timeSeconds * VERTICAL_SWAY_SPEED_RADIANS_PER_SECOND +
					index * VERTICAL_SWAY_PHASE_OFFSET_RADIANS,
			) *
				swayDistance +
			((apexY - baseY) / distanceToApex) * clickTravelDistance,
	];
};

export const createCursorDot = ({
	color,
	index,
	timeSeconds,
}: CursorDotInput): ConstellationDot => {
	const clickStrength = getClickStrength(timeSeconds);
	const highlightStrength = getHighlightStrength(index, timeSeconds);
	const [x, y] = getAnimatedCursorPosition(index, timeSeconds, clickStrength);

	return {
		brightness:
			BASE_BRIGHTNESS + HIGHLIGHT_BRIGHTNESS * highlightStrength + CLICK_BRIGHTNESS * clickStrength,
		color,
		glowStrength: Math.min(
			1,
			getGlowSparkStrength(
				highlightStrength,
				HIGHLIGHT_GLOW_CONCENTRATION_POWER,
				HIGHLIGHT_GLOW_STRENGTH,
			) + (index === CURSOR_APEX_POSITION_INDEX ? clickStrength * APEX_CLICK_GLOW_STRENGTH : 0),
		),
		scale: 1,
		x,
		y,
	};
};
